import React, { useEffect, useState } from 'react'
import axios from 'axios'

import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import Bar from '../ui/Bar'
import {
  Empty,
  IconEdit,
  LineSkeleton,
  Modal,
  Note
} from './peopleUI'
import './people.css'

/*
 * User Stats — the skill ratings a sport defines for one player.
 *
 * The API is untouched: GET admin/sports/:SportID gives the sport and its Tags,
 * GET admin/sports/stats/:uuid gives whatever has been rated so far, and
 * POST admin/sports/stats saves one rating.
 *
 * A striped table of numbers made it impossible to see where a player is strong.
 * The same numbers as bars answer that at a glance, and a tag nobody has rated
 * yet reads as an empty track rather than a blank cell. Each row keeps its own
 * edit control — a 40px target, visible without hovering.
 *
 * The old file also assumed both responses were well-formed and would throw a
 * white screen if a sport had no Tags or a tag went missing. Every read below
 * is guarded.
 */

const MAX = 100

export default function UserStats(props) {
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState({ SportName: '', Tags: '' })
  const [tags, setTags] = useState([])
  const [show, setShow] = useState(false)
  const [stats, setStats] = useState([])
  const [stat, setStat] = useState({ Value: 0 })

  const [failed, setFailed] = useState(false)
  const [saving, setSaving] = useState(false)
  const [note, setNote] = useState(null)

  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadData = () => {
    setIsLoading(true)
    setFailed(false)
    axios.get(process.env.REACT_APP_API_SERVER + 'admin/sports/' + props.SportID, {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        const sport = res.data && res.data.results ? res.data.results[0] : null
        if (!sport) {
          setIsLoading(false)
          setTags([])
          setData({ SportName: '', Tags: '' })
          return
        }
        loadStats(sport)
      }).catch(err => {
        setIsLoading(false)
        setFailed(true)
        console.log(err)
      })
  }

  const handleClose = () => setShow(false)

  const loadStats = (xstatsList) => {
    axios.get(process.env.REACT_APP_API_SERVER + 'admin/sports/stats/' + props.id, {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        const results = res.data && Array.isArray(res.data.results) ? res.data.results : []
        const tagList = String(xstatsList.Tags || '').length > 0
          ? String(xstatsList.Tags).split(',')
          : []

        /* A tag the player has never been rated on still needs a row. */
        tagList.forEach(s => {
          if (results.filter(item => item.StatName == s).length == 0) {
            results.push({
              StatName: s,
              Value: null,
              UserUUID: props.id
            })
          }
        })

        setStats(results)
        setData(xstatsList)
        setTags(tagList)
        setIsLoading(false)
        setShow(false)
      }).catch(err => {
        setIsLoading(false)
        setFailed(true)
        console.log(err)
      })
  }

  const rowFor = (name) => stats.filter(s => s.StatName == name)[0] || { StatName: name, Value: null, UserUUID: props.id }

  const changeValue = (item) => {
    setNote(null)
    setStat(rowFor(item))
    setShow(true)
  }

  const changeStatValue = (e) => {
    e.preventDefault()
    setSaving(true)

    axios.post(process.env.REACT_APP_API_SERVER + 'admin/sports/stats', stat, {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        setSaving(false)
        if (res.data.err == 0) {
          const xstats = stats
          xstats.forEach(item => {
            if (item.StatName == stat.StatName) {
              item.Value = stat.Value
            }
          })
          loadData()
        } else {
          setNote({ tone: 'crit', text: 'An error has occured' })
        }
      }).catch(err => {
        setSaving(false)
        setNote({ tone: 'crit', text: 'An error has occured' })
        console.log(err)
      })
  }

  const rated = tags.filter((t) => {
    const v = rowFor(t).Value
    return v !== null && v !== undefined && v !== ''
  }).length

  return (
    <Panel
      title="User Stats"
      subtitle={
        isLoading
          ? 'Loading…'
          : data.SportName
            ? data.SportName + ' · ' + rated + ' of ' + tags.length + ' rated'
            : undefined
      }
    >
      {note ? (
        <Note tone={note.tone} onDismiss={() => setNote(null)}>{note.text}</Note>
      ) : null}

      {isLoading ? (
        <LineSkeleton lines={5} />
      ) : failed ? (
        <Empty
          title="Stats could not be loaded"
          action={<Btn variant="accent" onClick={loadData}>Try again</Btn>}
        >
          The sport or its ratings did not answer. Nothing has been lost.
        </Empty>
      ) : tags.length === 0 ? (
        <Empty title="This sport defines no stats">
          Ratings come from the tags set on the sport. Add them under
          Παράμετροι → Αθλήματα and they appear here for every player.
        </Empty>
      ) : (
        <div className="pp-stats">
          {tags.map((item, index) => {
            const row = rowFor(item)
            const unset = row.Value === null || row.Value === undefined || row.Value === ''
            const label = String(item).trim()

            return (
              <div
                className={unset ? 'pp-stat-row pp-stat-unset' : 'pp-stat-row'}
                key={'Tag' + index}
              >
                <Bar
                  label={label}
                  value={unset ? 0 : Number(row.Value)}
                  display={unset ? '—' : row.Value}
                  max={MAX}
                />
                <button
                  type="button"
                  className="pp-rowbtn"
                  title={'Edit ' + label}
                  aria-label={'Edit ' + label}
                  onClick={() => changeValue(item)}
                >
                  <i><IconEdit /></i>
                </button>
              </div>
            )
          })}
        </div>
      )}

      <Modal
        open={show}
        title={String(stat.StatName || '').trim()}
        subtitle="A rating between 0 and 100"
        onClose={handleClose}
      >
        <form className="pp-form" onSubmit={changeStatValue}>
          <div className="pp-field">
            <label className="pp-label" data-req="1" htmlFor="pp-stat-value">Value</label>
            <input
              id="pp-stat-value"
              className="pp-input"
              required
              type="number"
              placeholder="value"
              min={0}
              max={MAX}
              value={stat.Value === null || stat.Value === undefined ? '' : stat.Value}
              onChange={(e) => setStat({ ...stat, Value: e.target.value })}
            />
            <div className="pp-hint">
              0 is the floor of the scale, 100 the top. Leaving a stat unrated is fine —
              it simply shows as an empty bar.
            </div>
          </div>

          <div className="pp-actions">
            <Btn type="button" onClick={handleClose}>Cancel</Btn>
            <Btn variant="accent" type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Btn>
          </div>
        </form>
      </Modal>
    </Panel>
  )
}
